import React, { useEffect, useState } from 'react';
import { FiSearch, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import { adminApi } from '../../services/adminApi';

function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (searchDebounced) params.search = searchDebounced;
    adminApi.getSuppliers(params)
      .then((res) => {
        if (res.success) {
          setSuppliers(res.data || []);
          setError(null);
        } else setError(res.message || 'Failed to load');
      })
      .catch((err) => {
        setError(err.message || 'Failed to load');
        setSuppliers([]);
      })
      .finally(() => setLoading(false));
  }, [searchDebounced]);

  const handleStatus = async (id, status) => {
    setUpdating(id);
    try {
      const res = await adminApi.updateSupplierStatus(id, status);
      if (res.success) {
        setSuppliers((prev) => prev.map((s) => (s.id === id ? { ...s, status } : s)));
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete supplier "${name || id}"? Voucher links to this supplier will be cleared.`)) return;
    setUpdating(id);
    try {
      const res = await adminApi.deleteSupplier(id);
      if (res.success) {
        setSuppliers((prev) => prev.filter((s) => s.id !== id));
        setError(null);
      } else {
        setError(res.message);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header admin-page-header-row">
        <div>
          <h1>Supplier Management</h1>
          <p>Manage supplier accounts (registered via WhatsApp)</p>
        </div>
      </div>
      <div className="admin-toolbar">
        <div className="admin-search">
          <FiSearch className="admin-search-icon" />
          <input
            type="search"
            placeholder="Search suppliers..."
            className="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="admin-cards-grid">
        {loading ? (
          <div className="admin-loading admin-loading-full" />
        ) : error ? (
          <p className="admin-error">{error}</p>
        ) : suppliers.length === 0 ? (
          <p className="admin-empty">No suppliers found</p>
        ) : (
          suppliers.map((s) => (
            <div key={s.id} className="admin-card">
              <div className="admin-card-header">
                <span className={`admin-badge admin-badge-${s.status}`}>{s.status}</span>
                <code className="admin-card-id">S{String(s.id).padStart(3, '0')}</code>
              </div>
              <h3 className="admin-card-title">{s.name}</h3>
              <p className="admin-card-meta">{s.contact || '-'}</p>
              <p className="admin-card-desc">{s.input_categories || '-'}</p>
              <div className="admin-card-actions">
                {s.status === 'pending' && (
                  <>
                    <button
                      className="admin-btn-icon admin-btn-success"
                      title="Approve"
                      onClick={() => handleStatus(s.id, 'active')}
                      disabled={updating === s.id}
                    >
                      <FiCheck />
                    </button>
                    <button
                      className="admin-btn-icon admin-btn-danger"
                      title="Reject"
                      onClick={() => handleStatus(s.id, 'rejected')}
                      disabled={updating === s.id}
                    >
                      <FiX />
                    </button>
                  </>
                )}
                <button
                  type="button"
                  className="admin-btn-icon admin-btn-danger"
                  title="Delete supplier"
                  onClick={() => handleDelete(s.id, s.name)}
                  disabled={updating === s.id}
                >
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default AdminSuppliers;
